import { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Check, Bell } from "lucide-react";

import { useConfirm } from "@/context/ConfirmContext";
function MultiSelectEquipos({ roles, seleccion, onToggle, testidPrefix }) {
  return (
    <div className="flex flex-wrap gap-2" data-testid={`${testidPrefix}-container`}>
      {roles.map((r) => {
        const activo = seleccion.includes(r.id);
        return (
          <button
            key={r.id}
            type="button"
            onClick={() => onToggle(r.id)}
            data-testid={`${testidPrefix}-${r.id}`}
            aria-pressed={activo}
            className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium border transition-colors ${
              activo
                ? "bg-slate-900 text-white border-slate-900"
                : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50"
            }`}
          >
            {activo && <Check size={15} strokeWidth={2.5} />}
            {r.nombre}
          </button>
        );
      })}
    </div>
  );
}

export default function Notificaciones() {
  const { user } = useAuth();
  const confirmar = useConfirm();
  const [roles, setRoles] = useState([]);
  const [modelo, setModelo] = useState([]);
  const [modeloInicial, setModeloInicial] = useState([]);
  const [muestra, setMuestra] = useState([]);
  const [busy, setBusy] = useState(false);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    Promise.all([
      api.get("/roles-map"),
      api.get("/config/notificaciones"),
    ])
      .then(([r, c]) => {
        setRoles(r.data || []);
        setModelo(c.data.modelo || []);
        setModeloInicial(c.data.modelo || []);
        setMuestra(c.data.muestra || []);
        setLoaded(true);
      })
      .catch((e) => toast.error(formatApiError(e.response?.data?.detail)));
  }, []);

  if (user && !user.es_admin) return <Navigate to="/" replace />;

  const toggle = (list, setList, id) =>
    setList(list.includes(id) ? list.filter((x) => x !== id) : [...list, id]);

  const guardar = async () => {
    // Blindaje: confirmar si se está quitando un rol de sistema que antes recibía correos.
    const SISTEMA = ["desarrollo", "diseno"];
    const quitados = modeloInicial.filter((id) => SISTEMA.includes(id) && !modelo.includes(id));
    if (quitados.length > 0) {
      const nombres = quitados.map((id) => roles.find((r) => r.id === id)?.nombre || id).join(", ");
      if (!(await confirmar({ titulo: "¿Quitar equipo de los correos?", mensaje: `Vas a dejar de enviar el correo de nuevas solicitudes de modelo al equipo: ${nombres}.`, accion: "Sí, quitar" }))) {
        return;
      }
    }
    setBusy(true);
    try {
      await api.put("/config/notificaciones", { modelo, muestra });
      setModeloInicial(modelo);
      toast.success("Configuración guardada");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="p-6 lg:p-8 max-w-3xl" data-testid="notificaciones-page">
      <div className="flex items-center gap-3 mb-2">
        <div className="h-10 w-10 rounded-xl bg-blue-600 flex items-center justify-center text-white">
          <Bell size={20} />
        </div>
        <div>
          <h1 className="font-head text-3xl font-extrabold tracking-tight">Notificaciones</h1>
          <p className="text-slate-500 mt-0.5">Define qué equipos reciben el correo de cada tipo de solicitud.</p>
        </div>
      </div>

      {loaded && (
        <div className="space-y-5 mt-6">
          <Card className="border-slate-200 shadow-sm p-6">
            <h2 className="font-semibold text-slate-800">Solicitud de modelo</h2>
            <p className="text-sm text-slate-500 mb-4">Equipos que recibirán el correo al crearse una nueva solicitud de modelo.</p>
            <MultiSelectEquipos roles={roles} seleccion={modelo} onToggle={(id) => toggle(modelo, setModelo, id)} testidPrefix="notif-modelo" />
            {modelo.length === 0 && <p className="text-xs text-amber-600 mt-3">Sin equipos seleccionados: no se enviará correo para solicitudes de modelo.</p>}
          </Card>

          <Card className="border-slate-200 shadow-sm p-6">
            <h2 className="font-semibold text-slate-800">Solicitud de muestra</h2>
            <p className="text-sm text-slate-500">Las notificaciones de muestra siguen un flujo automático y no se configuran aquí: al crearse notifican a quienes pueden autorizar; al aprobarse, al equipo de Diseño; al rechazarse, al solicitante con el motivo.</p>
          </Card>

          <div className="flex justify-end">
            <Button className="rounded-lg" onClick={guardar} disabled={busy} data-testid="notif-save-button">
              Guardar cambios
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
